import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./index.scss";
import { useSelector } from "react-redux";
import CustomActionMenu from "@components/CustomActionMenu/CustomActionMenu";
import { CreateModalContext } from "layouts/appLayout/layout";
import {
  useGetClientsQuery,
  useToggleClientStatusMutation,
} from "store/client/clientApi";
import CreateClientComponent from "@components/CreateClientComponent/CreateClientComponent";
import Loader from "@components/Loader/Loader";
import CustomHeader from "@components/CustomHeader/CustomHeader";
import CustomTable from "@components/CustomTable/CustomTable";
import { toast } from "react-toastify";
import CustomSwitch from "@components/CustomSwitch/CustomSwitch";
import ReactPaginate from "react-paginate"; // ✅ import pagination

const ClientsPage = () => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;

  const { data: clients, isLoading: isLoadingClients } = useGetClientsQuery(
    companyId,
    { skip: !companyId }
  );

  const [searchText, setSearchText] = useState("");
  const { toggleCreateModal } = useContext(CreateModalContext);
  const [toggleClientStatus] = useToggleClientStatusMutation();

  // ✅ Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5; // nombre d’éléments par page

  // ✅ Permissions
  const canEditClient =
    currentUser?.roleId?.permissions.includes("EDIT_CLIENT");
  const canToggleClient =
    currentUser?.roleId?.permissions.includes("EDIT_CLIENT");

  const handleToggle = async (id) => {
    if (!canToggleClient) return toast.error(t("client.no_permission"));
    try {
      await toggleClientStatus(id).unwrap();
      toast.success(t("client.status_changed"));
    } catch {
      toast.error(t("client.status_change_error"));
    }
  };

  const headers = [
    { key: "name", label: t("client.create_form.name") },
    { key: "email", label: t("client.create_form.email") },
    { key: "address", label: t("client.create_form.address") },
    { key: "phone", label: t("client.create_form.phone") },
    ...(canToggleClient
      ? [{ key: "enabled", label: t("client.create_form.status") }]
      : []),
  ];

  const renderCell = (key, client) => {
    if (key === "enabled")
      return (
        <CustomSwitch
          checked={client.enabled}
          onChange={() => handleToggle(client._id)}
        />
      );
    return client[key];
  };

  const handleEditClient = (client) => {
    if (!canEditClient) return toast.error(t("client.no_permission"));
    toggleCreateModal(
      <CreateClientComponent
        onClose={() => toggleCreateModal(<></>)}
        client={client}
      />,
      t("client.create_form.title")
    );
  };

  const showActionColumn = canEditClient || canToggleClient;

  const actionMenu = showActionColumn
    ? (client) => (
        <CustomActionMenu
          data={client}
          actions={[
            ...(canEditClient
              ? [
                  {
                    label: t("client.actions.edit"),
                    onClick: () => handleEditClient(client),
                  },
                ]
              : []),
            ...(canToggleClient
              ? [
                  {
                    label: client.enabled
                      ? t("client.actions.deactivate")
                      : t("client.actions.activate"),
                    onClick: () => handleToggle(client._id),
                  },
                ]
              : []),
          ]}
        />
      )
    : undefined;

  const onSearchClient = (text) => setSearchText(text.toLowerCase());

  const filteredClients = useMemo(() => {
    if (!clients?.data) return [];
    if (searchText.length <= 1) return clients.data;
    return clients.data.filter((client) => {
      const name = client.name?.toLowerCase() || "";
      const email = client.email?.toLowerCase() || "";
      return name.includes(searchText) || email.includes(searchText);
    });
  }, [clients?.data, searchText]);

  // ✅ Découpage pagination
  const offset = currentPage * itemsPerPage;
  const currentItems = filteredClients.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(filteredClients.length / itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  if (isLoadingClients) return <Loader />;

  return (
    <div className="p-5" style={{ overflowX: "auto" }}>
      <CustomHeader
        title={t("client.header_title")}
        dataLength={filteredClients?.length}
        onSearch={onSearchClient}
      />
      <CustomTable
        headers={headers}
        data={currentItems} // ✅ pagination appliquée
        rowKey="_id"
        renderCell={renderCell}
        actionMenu={actionMenu}
      />

      {/* ✅ Pagination en bas */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
        <ReactPaginate
          previousLabel={"←"}
          nextLabel={"→"}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={1}
          pageRangeDisplayed={2}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          activeClassName={"active"}
          forcePage={currentPage}
        />
      </div>
    </div>
  );
};

export default ClientsPage;
